import { LoadingButton } from '@mui/lab';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import TextField from 'common/forms/TextField';
import { useState } from 'react';
import { Form } from 'react-final-form';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { editOrderShipment } from 'store/thunks/patientThunks';
import { handleError } from 'store/thunks/errorHandlerThunks';
import { useSnackbar } from 'notistack';
import { updateProcessedExternalOrdersFilter } from 'store/thunks/workQueueThunks';

const isShipstationValidationError = (message = '') =>
  /Tracking Number .* not found for the order with order Id .* in shipstation/i.test(message) ||
  /Carrier code .* not found for the order with order Id .* in shipstation/i.test(message);

const EditShipmentDialog = ({
  open,
  onClose,
  orderShipmentId,
  mpi,
  spOrderId,
  trackingNumber,
  carrierCode,
  orderId,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const filter = useSelector(({ workQueue }) => workQueue.processedExternalOrders.filter ?? {});

  const initialShipment = !orderId ? { spOrderId, trackingNumber, carrierCode } : { orderId };

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [pendingValues, setPendingValues] = useState(null);

  const handleClose = (_, reason) => {
    if (reason !== 'backdropClick') {
      setErrorMessage('');
      setPendingValues(null);
      onClose();
    }
  };

  const submitShipment = ({ formData, validationMode }) => {
    setLoading(true);

    return dispatch(
      editOrderShipment({
        input: {
          ...formData,
          ...(orderShipmentId && { orderShipmentId }),
          ...(validationMode && { validationMode }),
        },
        mpi,
        suppressError: true,
      })
    )
      .then((updatedShipment) => {
        if (updatedShipment) {
          enqueueSnackbar(
            validationMode === 'MANUAL_OVERRIDE'
              ? 'Order shipment updated manually'
              : 'Order shipment updated',
            { variant: 'success' }
          );
          dispatch(updateProcessedExternalOrdersFilter({ filter }));
          onClose();
          setErrorMessage('');
          setPendingValues(null);
        }
      })
      .catch((error) => {
        const message = error?.response?.errors?.[0]?.message || error?.message || 'Update failed';

        if (validationMode !== 'MANUAL_OVERRIDE' && isShipstationValidationError(message)) {
          setErrorMessage(message);
          setPendingValues(formData);
        } else {
          dispatch(handleError({ error }));
          handleClose();
        }
      })
      .finally(() => setLoading(false));
  };

  const handleManualUpdate = () => {
    if (!pendingValues) return;
    submitShipment({ formData: pendingValues, validationMode: 'MANUAL_OVERRIDE' });
  };

  return (
    <>
      <Dialog open={open && !errorMessage} onClose={handleClose} fullWidth>
        <DialogTitle>
          {orderShipmentId
            ? `Edit Shipment - ${orderShipmentId}`
            : `Edit Shipment of Order - ${orderId}`}
        </DialogTitle>

        <Form
          initialValues={initialShipment}
          onSubmit={(formData) => submitShipment({ formData })}
          render={({ handleSubmit, invalid }) => (
            <form noValidate onSubmit={handleSubmit}>
              <DialogContent>
                {orderId && (
                  <TextField
                    id='Shipments-EditShipment-orderId-input'
                    label='Order Id'
                    name='orderId'
                    required
                  />
                )}
                <TextField
                  id='Shipments-EditShipment-nameOnAddress-input'
                  label='Shipstation Order Id'
                  name='spOrderId'
                  required
                  autoFocus
                />
                <TextField
                  id='Shipments-EditShipment-addressLine1-input'
                  label='Tracking Number'
                  name='trackingNumber'
                  required
                />
                <TextField
                  id='Shipments-EditShipment-addressLine2-input'
                  label='Carrier Code'
                  name='carrierCode'
                  required
                />
                {orderId && (
                  <TextField
                    id='Shipments-EditShipment-order_shipment_id-input'
                    label='Order Shipment Id'
                    name='orderShipmentId'
                    required
                  />
                )}
              </DialogContent>

              <DialogActions>
                <Button variant='outlined' color='secondary' onClick={handleClose}>
                  Cancel
                </Button>
                <LoadingButton
                  loading={loading}
                  variant='contained'
                  disabled={invalid}
                  type='submit'
                >
                  Save
                </LoadingButton>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>

      <Dialog open={!!errorMessage} onClose={handleClose} fullWidth maxWidth='sm'>
        <DialogTitle>Order Shipment Update Manually</DialogTitle>
        <DialogContent>
          <Alert severity='error'>{errorMessage}</Alert>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' color='secondary' onClick={handleClose}>
            Cancel
          </Button>
          <LoadingButton
            loading={loading}
            variant='contained'
            color='primary'
            id='Shipments-EditShipment-updateManually-button'
            onClick={handleManualUpdate}
          >
            Update Manually
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

EditShipmentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  orderShipmentId: PropTypes.string,
  mpi: PropTypes.string.isRequired,
  spOrderId: PropTypes.string,
  trackingNumber: PropTypes.string,
  carrierCode: PropTypes.string,
  orderId: PropTypes.string,
};

EditShipmentDialog.defaultProps = {
  orderShipmentId: '',
  orderId: '',
  spOrderId: '',
  trackingNumber: '',
  carrierCode: '',
};

export default EditShipmentDialog;
